/**
 * Vulnerability Column Definitions for SelectionTable
 *
 * Defines 3 columns:
 * 1. Status - Risk status chip (first for triage workflow)
 * 2. Severity - Priority badge with custom sorting (Critical first)
 * 3. Vulnerability - Name with metadata
 *
 * Order rationale: Status-first ordering supports triage workflow where users
 * prioritize by action state before severity assessment.
 */

import type { ColumnDef, SortingFn, SortingState } from '@tanstack/react-table';

import type { SearchableListItem } from '@/sections/attacks/attackPaths/components/wizards/hooks';

import { NameCell, SeverityCell, StatusCell } from './shared';

/**
 * Priority order for sorting (Critical=0 should come first in ascending)
 */
const PRIORITY_ORDER: Record<number, number> = {
  0: 0, // Critical
  10: 1, // High
  20: 2, // Medium
  30: 3, // Low
  40: 4, // Info
};

/**
 * Custom sorting function for severity column.
 * Sorts by priority order: Critical -> High -> Medium -> Low -> Info
 */
const severitySortingFn: SortingFn<SearchableListItem> = (rowA, rowB) => {
  const priorityA = rowA.original.priority ?? 40;
  const priorityB = rowB.original.priority ?? 40;

  const orderA = PRIORITY_ORDER[priorityA] ?? 5;
  const orderB = PRIORITY_ORDER[priorityB] ?? 5;

  return orderA - orderB;
};

/**
 * Column definitions for vulnerability selection table
 */
export const vulnerabilityColumns: ColumnDef<SearchableListItem>[] = [
  {
    id: 'status',
    accessorFn: row => row.status,
    header: 'Status',
    cell: ({ row }) => (
      <StatusCell status={row.original.status} entityType="risk" />
    ),
    size: 95,
  },
  {
    id: 'severity',
    accessorFn: row => row.priority,
    header: 'Severity',
    cell: ({ row }) => {
      const priority = row.original.priority;
      return <SeverityCell priority={priority} />;
    },
    sortingFn: severitySortingFn,
    size: 80,
  },
  {
    id: 'name',
    accessorFn: row => row.name,
    header: 'Vulnerability',
    cell: ({ row }) => (
      <div className="truncate" title={row.original.name}>
        <NameCell name={row.original.name} metadata={row.original.metadata} />
      </div>
    ),
    sortingFn: 'alphanumeric',
    meta: { flexible: true },
  },
];

/**
 * Default sort state: severity ascending (Critical first)
 */
export const vulnerabilityDefaultSort: SortingState = [
  { id: 'severity', desc: false },
];
